/**
 * CyclesScreen — Ciclos de Desenvolvimento (Fase 5).
 *
 * Fiel ao protótipo (StepCycles): stepper de Nº de ciclos + colunas encadeadas
 * (kanban) por ciclo, cada uma com itens do backlog (herdados por cycle_number)
 * e pontos. Ciclo 0 = Fundação Técnica (pré-requisito). Dados da memória/contexto
 * do projeto (GET /backlog). "Replanejar" é determinístico (sem IA).
 */
import { C } from '@/theme/palette';
import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProjects } from '@/api/projects';
import { useBacklog, useReplanCycles } from '@/api/backlog';
import { BACKLOG_TYPE, effectivePoints, leavesByCycle } from './backlog/backlogModel';

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

export function CyclesScreen({ projectId: wizardProjectId } = {}) {
  const [searchParams] = useSearchParams();
  const urlProject = searchParams.get('project');
  const { data: projectsRaw } = useProjects();
  const projects = useMemo(
    () => (Array.isArray(projectsRaw) ? projectsRaw : projectsRaw?.items ?? []),
    [projectsRaw],
  );
  const projectId = wizardProjectId || urlProject || projects?.[0]?.id || null;
  const { data } = useBacklog(projectId);
  const replan = useReplanCycles(projectId);
  const cycles = data?.cycles ?? [];
  const epics = data?.epics ?? [];
  const byCycle = useMemo(() => leavesByCycle(epics), [epics]);

  const [n, setN] = useState(3);
  useEffect(() => { setN(Math.max(1, cycles.filter((c) => c.number > 0).length || 3)); }, [data]); // eslint-disable-line

  if (!projectId) return <div style={{ padding: 40, color: C.dim }}>Nenhum projeto selecionado.</div>;

  const changeN = (delta) => {
    const nn = Math.max(1, Math.min(5, n + delta));
    if (nn === n) return;
    setN(nn);
    replan.mutate(nn);
  };
  const statsFor = (num) => {
    const items = byCycle.get(num) || [];
    return { items, pts: items.reduce((a, l) => a + effectivePoints(l), 0) };
  };

  return (
    <div className="sp-page" style={{ color: C.text }}>
      <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>Ciclos de Desenvolvimento</h2>
      <p style={{ color: C.dim, maxWidth: '80ch', marginTop: 10, fontSize: 13.5, lineHeight: 1.5 }}>
        O backlog é distribuído em ciclos encadeados: o <strong style={{ color: C.text }}>Ciclo 0</strong> entrega
        a fundação técnica (pré-requisito de tudo) e os demais fatiam o desenvolvimento em Sprint Backlogs
        respeitando as dependências.
      </p>

      {/* Controle: Nº de ciclos (stepper) */}
      <div style={{ marginTop: 16, border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12,
        padding: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 13.5, color: C.text, fontWeight: 600 }}>📅 Nº de ciclos de desenvolvimento</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>(além do Ciclo 0 · Fundação)</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={() => changeN(-1)} disabled={replan.isPending || n <= 1}
            style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface2,
              color: C.text, fontSize: 18, cursor: replan.isPending || n <= 1 ? 'default' : 'pointer', opacity: n <= 1 ? 0.4 : 1 }}>−</button>
          <span style={{ fontFamily: 'monospace', fontSize: 26, color: C.cyber, minWidth: 26, textAlign: 'center' }}>{n}</span>
          <button onClick={() => changeN(1)} disabled={replan.isPending || n >= 5}
            style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface2,
              color: C.text, fontSize: 18, cursor: replan.isPending || n >= 5 ? 'default' : 'pointer', opacity: n >= 5 ? 0.4 : 1 }}>+</button>
        </div>
      </div>

      {replan.isError && <div style={{ marginTop: 10, color: C.digital, fontSize: 13 }}>Falha ao replanejar.</div>}
      {cycles.length === 0 && (
        <div style={{ marginTop: 24, padding: 28, border: `1px dashed ${C.border}`, borderRadius: 12,
          color: C.dim, textAlign: 'center' }}>
          Sem ciclos ainda. Gere o <strong style={{ color: C.text }}>Backlog</strong> primeiro (ele cria os ciclos), depois ajuste aqui.
        </div>
      )}

      {/* Colunas encadeadas (kanban) */}
      {cycles.length > 0 && (
        <div style={{ position: 'relative', marginTop: 18 }}>
          {replan.isPending && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 2, background: 'rgba(12,12,12,0.6)',
              display: 'grid', placeItems: 'center', borderRadius: 12, color: C.cyber, fontSize: 13 }}>
              ↻ Replanejando ciclos…
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, alignItems: 'stretch' }}>
            {cycles.map((c, i) => {
              const st = statsFor(c.number);
              const accent = c.number === 0 ? C.quantum : C.cyber;
              return (
                <div key={c.number} style={{ display: 'flex', alignItems: 'stretch', gap: 6 }}>
                  <div style={{ minWidth: 220, maxWidth: 240, background: C.surface, border: `1px solid ${C.border}`,
                    borderTop: `3px solid ${accent}`, borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>
                      Ciclo {c.number} · {c.number === 0 ? 'Fundação Técnica' : c.theme}
                    </div>
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                      {st.items.length} itens · {st.pts} pts
                      {c.status && c.status !== 'planned' ? ` · ${c.status}` : ''}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                      {st.items.length === 0 && <div style={{ fontSize: 11.5, color: C.muted }}>—</div>}
                      {st.items.map((l) => {
                        const t = BACKLOG_TYPE[l.type === 'task' ? 'task' : 'story'];
                        return (
                          <div key={l.id} style={{ background: C.black, border: `1px solid ${C.border}`,
                            borderLeft: `2px solid ${t.color}`, borderRadius: 7, padding: '7px 9px' }}>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <span style={{ fontFamily: 'monospace', fontSize: 9.5, color: C.muted }}>{String(l.id).slice(0, 6)}</span>
                              <span style={{ fontFamily: 'monospace', fontSize: 8.5, color: t.color, letterSpacing: '0.05em' }}>{t.label}</span>
                              {l.reuse && <span style={{ fontSize: 9, color: C.quantum }}>♻</span>}
                            </div>
                            <div style={{ fontSize: 11.5, color: C.dim, marginTop: 3, lineHeight: 1.3 }}>{l.title}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {i < cycles.length - 1 && (
                    <div style={{ display: 'grid', placeItems: 'center', color: C.muted, fontSize: 18 }}>›</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ marginTop: 14, fontSize: 12, color: C.muted, display: 'flex', gap: 7, alignItems: 'flex-start' }}>
        <span style={{ color: C.cyber }}>ⓘ</span>
        Os Sprint Backlogs são encadeados: cada ciclo só inicia quando o anterior passa pela validação do Squad de Qualidade.
      </div>
    </div>
  );
}
